<template>
  <standard-dialog
    v-model="model"
    :title="editedItem.id ? $t('i_m') : $t('n_m')"
    :subtitle="editedItem?.id ? `ID: ${editedItem.id}` : ''"
    :max-width="1000"
    :fullscreen="device.isMobile"
    icon="mdi-food"
    @save="onSave"
    :disabled="!valid"
    :loading="loading"
  >
    <v-form v-model="valid" ref="form">
      <!-- LANGUAGE TABS -->
      <v-tabs v-model="activeLanguageTab" color="primary" class="mb-4">
        <v-tab v-for="lang in availableLanguages" :key="lang.code">
          {{ lang.text }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeLanguageTab">
        <v-tab-item v-for="lang in availableLanguages" :key="lang.code">
          <!-- BASIC INFO -->
          <food-basic-info
            :edited-item="editedItem"
            :categories="categories"
            :currency="$store.state.restaurant.settings?.price || ''"
            :read-only-category="readOnlyCategory"
            :language="lang.code"
          />

          <!-- SIZES & PRICES SECTION -->
          <v-card outlined v-if="localSizes.length > 0">
            <v-card-subtitle class="d-flex align-center pb-2">
              <v-icon small color="primary" class="mr-2">mdi-resize</v-icon>
              <span class="font-weight-bold primary--text">{{ $t("meal_size") }}</span>
            </v-card-subtitle>
            <v-card-text>
              <v-row dense>
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                  v-for="(size, index) in localSizes"
                  :key="size.size_id"
                >
                  <v-card
                    outlined
                    class="size-mini-card"
                    :class="{ 'size-active': size.is_true }"
                  >
                    <v-card-text class="pa-3">
                      <div class="d-flex align-center justify-space-between mb-2">
                        <v-switch
                          v-model="size.is_true"
                          :label="size.size.name"
                          dense
                          hide-details
                          color="primary"
                          @change="onSizeToggle(index)"
                        />
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div v-bind="attrs" v-on="on">
                              <v-checkbox
                                v-show="size.is_true"
                                v-model="size.default_size"
                                :true-value="1"
                                :false-value="0"
                                hide-details
                                @change="setDefaultSize(index, $event)"
                              />
                            </div>
                          </template>
                          <span>Osnovna cena koja se prikazuje na jelu</span>
                        </v-tooltip>
                      </div>

                      <v-expand-transition>
                        <div v-if="size.is_true">
                          <v-divider class="mb-3"></v-divider>
                          <text-field
                            v-model="size.price"
                            type="number"
                            :label="$t('price')"
                            :suffix="$store.state.restaurant.settings?.price || ''"
                            outlined
                            dense
                            hide-details
                          ></text-field>
                          <text-field
                            v-model="size.last_price"
                            type="number"
                            label="Stara cena"
                            :suffix="$store.state.restaurant.settings?.price || ''"
                            outlined
                            dense
                            hide-details
                            class="mt-2"
                          ></text-field>
                        </div>
                      </v-expand-transition>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- MENU TYPE -->
          <food-menu-type :edited-item="editedItem" :language="lang.code" />

          <!-- LABELS -->
          <food-labels :edited-item="editedItem" :language="lang.code" />

          <!-- COMPOSITION SECTION -->
          <food-grocery-composition :edited-item="editedItem" :language="lang.code" />

          <!-- PACKAGE (samo na osnovnom jeziku) -->
          <food-package v-if="lang.code === 'default'" :edited-item="editedItem" />

          <!-- SIDE DISHES SECTION -->
          <food-side-dishes
            :edited-item="editedItem"
            :side-dishes="sideDishes"
            :language="lang.code"
          />

          <food-package-rules
            v-if="lang.code === 'default' && editedItem.is_package"
            :edited-item="editedItem"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-form>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasSnackbar from "@/mixins/hasSnackbar";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import FoodBasicInfo from "./sections/FoodBasicInfo.vue";
import FoodMenuType from "./sections/FoodMenuType.vue";
import FoodLabels from "./sections/FoodLabels.vue";
import FoodGroceryComposition from "./sections/FoodGroceryComposition.vue";
import FoodSideDishes from "./sections/FoodSideDishes.vue";
import FoodPackage from "./sections/FoodPackage.vue";
import FoodPackageRules from "./sections/FoodPackageRules.vue";

export default {
  mixins: [hasModel, hasValidation, hasSnackbar],
  props: {
    editedItem: {
      type: Object,
      required: true,
    },
    categories: {
      type: Array,
      required: true,
    },
    sideDishes: {
      type: Array,
      default: () => [],
    },
    readOnlyCategory: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    StandardDialog,
    FoodBasicInfo,
    FoodMenuType,
    FoodLabels,
    FoodGroceryComposition,
    FoodSideDishes,
    FoodPackage,
    FoodPackageRules,
  },
  data: () => ({
    activeLanguageTab: 0,
    localSizes: [],
  }),
  methods: {
    initLocalSizes() {
      const sizePrices = this.editedItem?.size_prices || [];
      this.localSizes = sizePrices.map((sp) => ({
        ...sp,
        size: { ...(sp.size || {}) },
        is_true: !!sp.price,
        default_size: sp.price && sp.default_size ? 1 : 0,
      }));

      // Uvek tacno jedan default medju aktivnim velicinama
      const active = this.localSizes.filter((s) => s.is_true);
      if (active.length && !active.some((s) => s.default_size === 1)) {
        active[0].default_size = 1;
      }
    },
    onSizeToggle(index) {
      const size = this.localSizes[index];
      if (!size.is_true) {
        size.default_size = 0;
      }
      const active = this.localSizes.filter((s) => s.is_true);
      if (active.length && !active.some((s) => s.default_size === 1)) {
        active[0].default_size = 1;
      }
    },
    setDefaultSize(index, value) {
      if (value === 1) {
        this.localSizes.forEach((s, i) => {
          if (i !== index) {
            s.default_size = 0;
          }
        });
      } else {
        // Default se ne moze skinuti direktno — samo izborom druge velicine
        this.$nextTick(() => {
          this.localSizes[index].default_size = 1;
        });
      }
    },
    onSave() {
      this.$emit(
        "save",
        this.localSizes.map((s) => ({ ...s }))
      );
    },
  },
  computed: {
    availableLanguages() {
      // Filter languages based on restaurant settings (same logic as LanguageSelector)
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];

      const def = [
        {
          code: "default",
          text: "default",
          locale: "default",
          flag: "🇷",
          script: "default",
        },
      ];

      if (restaurantLanguages.length === 0) {
        // If no restaurant languages configured, return all available languages
        let data = this.getAvailableLanguages();
        return def.concat(data);
      }

      // Filter available languages to only include those configured for the restaurant
      let data = this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      );
      return def.concat(data);
    },
  },
  watch: {
    editedItem: {
      immediate: true,
      handler() {
        this.initLocalSizes();
      },
    },
    model(model) {
      if (model) {
        this.initLocalSizes();
      } else {
        this.$refs.form?.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
/* Card styling */
.v-card {
  border-radius: 8px;
}

.v-card.outlined {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

/* Size cards */
.size-mini-card {
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.size-mini-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.size-mini-card.size-active {
  border-color: var(--v-primary-base);
  background-color: rgba(var(--v-primary-base), 0.02);
}
</style>
